import { ref } from 'vue'
import { getApiBaseUrl } from './serverConfig'

// Type definitions for fetch API
type RequestInit = globalThis.RequestInit

// API_BASE now reads from localStorage (user configured) first, then env, then default
// This is a getter function to always get the latest configured URL
const getApiBase = () => getApiBaseUrl()

// For backward compatibility, we still export API_BASE but it will be re-evaluated on each request
let API_BASE = getApiBase()

// Function to refresh API_BASE (called after server config changes)
export function refreshApiBase() {
  API_BASE = getApiBase()
}

const DEFAULT_CACHE_TTL = Number((import.meta as any).env?.VITE_API_CACHE_TTL_MS ?? 15000) || 15000

// Frontend should be able to support both token-based and Laravel Sanctum cookie-auth flows.
// Toggle via VITE_USE_SANCTUM in your environment (.env.local)
export const USE_SANCTUM = Boolean(
  (import.meta as any).env?.VITE_USE_SANCTUM === 'true' ||
    (import.meta as any).env?.VITE_USE_SANCTUM === true,
)

type ApiFetchOptions = globalThis.RequestInit & {
  cacheTtl?: number
  forceRefresh?: boolean
}

interface CacheEntry {
  timestamp: number
  ttl: number
  data?: any
  promise?: Promise<any>
}

interface OfflineRequest {
  url: string
  options: globalThis.RequestInit
  timestamp: number
}

const requestCache = new Map<string, CacheEntry>()
const offlineQueue = ref<OfflineRequest[]>([])

// Enhanced API cache with intelligent caching
class ApiCache {
  private cache = new Map<string, CacheEntry>()
  private timestamps = new Map<string, number>()

  set(key: string, data: any, ttl: number = 300000) {
    this.cache.set(key, data)
    this.timestamps.set(key, Date.now() + ttl)
  }

  get(key: string) {
    const expiry = this.timestamps.get(key)
    if (expiry && expiry > Date.now()) {
      return this.cache.get(key)
    }
    this.cache.delete(key)
    this.timestamps.delete(key)
    return null
  }

  delete(key: string) {
    this.cache.delete(key)
    this.timestamps.delete(key)
  }

  invalidatePattern(pattern: RegExp) {
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.cache.delete(key)
        this.timestamps.delete(key)
      }
    }
  }

  clear() {
    this.cache.clear()
    this.timestamps.clear()
  }
}

const apiCache = new ApiCache()

function tokenKey() {
  return 'api_token'
}

function readCookie(name: string) {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)'))
  return match ? match.pop() || null : null
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(tokenKey(), token)
  else localStorage.removeItem(tokenKey())
}

export function getToken(): string | null {
  return localStorage.getItem(tokenKey())
}

function normalizeUrl(path: string): string {
  // Always get fresh API_BASE in case it was changed
  const currentBase = getApiBase()
  const trimmedBase = currentBase.replace(/\/+$/, '')
  const sanitizedPath = path.startsWith('http') ? path : path.replace(/^\/+/, '')
  if (path.startsWith('http')) return sanitizedPath
  return `${trimmedBase}/${sanitizedPath}`
}

async function buildHeaders() {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  // If using Sanctum session flow, include the XSRF token header (fetch doesn't set it automatically)
  if (USE_SANCTUM) {
    const xsrf = readCookie('XSRF-TOKEN')
    if (xsrf) headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrf)
  }

  return headers
}

function clonePayload<T>(data: T): T {
  if (data === null || typeof data !== 'object') return data
  try {
    // structuredClone is widely supported in modern browsers
    return structuredClone(data)
  } catch {
    return JSON.parse(JSON.stringify(data))
  }
}

function getCacheKey(method: string, url: string): string {
  return `${method}:${url}`
}

function invalidateCacheEntry(cacheKey: string) {
  requestCache.delete(cacheKey)
}

export function invalidateApiCache(path?: string | string[] | RegExp) {
  if (!path) {
    requestCache.clear()
    apiCache.clear()
    return
  }

  const targets = Array.isArray(path) ? path : [path]

  for (const [key] of requestCache) {
    const matched = targets.some((target) => {
      if (
        typeof target === 'object' &&
        target !== null &&
        'test' in target &&
        typeof target.test === 'function'
      ) {
        // Type guard for RegExp
        return (target as RegExp).test(key)
      }
      const normalized = normalizeUrl(target as string)
      return key.startsWith(`GET:${normalized}`)
    })

    if (matched) {
      requestCache.delete(key)
      // Also invalidate in smart cache
      apiCache.delete(key)
    }
  }
}

// Process offline queue when back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    console.log('Network online, processing queued requests...')
    const queue = [...offlineQueue.value]
    offlineQueue.value = []

    for (const request of queue) {
      try {
        await fetch(request.url, request.options)
        console.log('Successfully synced offline request:', request.url)
      } catch (err) {
        console.error('Failed to sync offline request:', err)
        // Re-queue failed requests
        offlineQueue.value.push(request)
      }
    }
  })
}

export async function apiFetch(path: string, opts: ApiFetchOptions = {}) {
  const { cacheTtl, forceRefresh, ...restOpts } = opts
  const url = normalizeUrl(path)
  const method = (restOpts.method ?? 'GET').toString().toUpperCase()
  const ttl = cacheTtl ?? DEFAULT_CACHE_TTL

  const shouldCache =
    method === 'GET' &&
    forceRefresh !== true &&
    restOpts.cache !== 'no-store' &&
    restOpts.cache !== 'reload'

  const headers = await buildHeaders()

  // FORCE FRESH DATA: Add anti-cache headers if forceRefresh is true
  if (forceRefresh) {
    headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
    headers['Pragma'] = 'no-cache'
    headers['Expires'] = '0'
  }

  const fetchOpts: globalThis.RequestInit = { ...restOpts, headers }

  // FORCE FRESH DATA: Add timestamp to URL to bypass browser cache
  let fetchUrl: string
  if (forceRefresh && method === 'GET') {
    const separator = url.includes('?') ? '&' : '?'
    // Use _t parameter to make URL unique
    fetchUrl = `${url}${separator}_t=${Date.now()}`
  } else {
    fetchUrl = url
  }

  if (USE_SANCTUM) {
    fetchOpts.credentials = fetchOpts.credentials ?? 'include'
    if (!getToken()) delete (fetchOpts.headers as any).Authorization
  }

  const cacheKey = getCacheKey(method, url) // Keep original URL for cache key to properly invalidate later

  // Check smart cache first
  if (shouldCache) {
    const cached = apiCache.get(cacheKey)
    if (cached) {
      return Promise.resolve(clonePayload(cached))
    }
  }

  // Check legacy cache
  if (shouldCache) {
    const entry = requestCache.get(cacheKey)
    if (entry?.promise) {
      return entry.promise
    }
    if (entry && Date.now() - entry.timestamp < entry.ttl && entry.data !== undefined) {
      return Promise.resolve(clonePayload(entry.data))
    }
  }

  const networkPromise = (async () => {
    let resp: Response
    try {
      resp = await fetch(fetchUrl, fetchOpts)
    } catch (err) {
      // Handle offline scenario
      if (!navigator.onLine && method !== 'GET') {
        // Queue for later when online
        offlineQueue.value.push({
          url: fetchUrl, // Use the actual URL attempted
          options: fetchOpts,
          timestamp: Date.now(),
        })
        throw new Error('Request queued for when online')
      }
      throw err
    }

    // Try JSON; if empty body return null
    const text = await resp.text()
    let json: any = null
    if (text) {
      try {
        json = JSON.parse(text)
        if (typeof json === 'string') {
          json = { message: json }
        }
      } catch (e) {
        json = { message: text }
      }
    }

    if (resp.status === 401) {
      setToken(null)

      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        const currentPath = window.location.pathname
        window.location.href = '/login?redirect=' + encodeURIComponent(currentPath)
      }

      const err = new Error('Unauthorized. Please login.')
      ;(err as any).status = 401
      ;(err as any).data = json
      throw err
    }

    if (resp.status === 429) {
      const err = new Error('Too many requests. Please try again later.')
      ;(err as any).status = 429
      ;(err as any).data = json
      throw err
    }

    if (!resp.ok) {
      // Extract error message safely
      let errorMessage = resp.statusText || 'An error occurred'

      if (json) {
        if (typeof json === 'string') {
          errorMessage = json
        } else if (typeof json === 'object' && json !== null) {
          // Try multiple possible error message fields - ensure they are strings
          if (typeof json.message === 'string' && json.message) {
            errorMessage = json.message
          } else if (typeof json.error === 'string' && json.error) {
            errorMessage = json.error
          } else {
            // Handle Laravel validation errors
            if (json.errors && typeof json.errors === 'object' && json.errors !== null) {
              try {
                const errorArray = Object.values(json.errors)
                  .flat()
                  .filter((item: any) => typeof item === 'string')
                if (errorArray.length > 0) {
                  errorMessage = errorArray.join(', ')
                }
              } catch (e) {
                // If extraction fails, fall through to default message
                console.warn('Failed to extract error array:', e)
              }
            }

            // If message is still not found, try to stringify for debugging
            if (!errorMessage || errorMessage === resp.statusText) {
              try {
                const jsonStr = JSON.stringify(json)
                errorMessage = `Server error (${resp.status}): ${jsonStr.substring(0, 200)}`
              } catch (e) {
                errorMessage = `Server error (${resp.status})`
              }
            }
          }
        }
      }

      // Ensure errorMessage is always a string and sanitize it
      if (typeof errorMessage !== 'string') {
        errorMessage = String(errorMessage || 'An error occurred')
      }

      // Sanitize error message - ensure it's a clean string
      errorMessage = String(errorMessage).trim()
      if (!errorMessage) {
        errorMessage = `Server error (${resp.status})`
      }

      // For 500 errors, provide a user-friendly message but keep technical details
      // Log the actual error for debugging
      if (resp.status === 500) {
        console.error('Server error (500) - Technical details:', {
          url,
          status: resp.status,
          errorMessage,
          responseData: json,
        })
        // Keep the original error message as it may contain useful debugging info
        // The stores will show this to users, which is acceptable for now
      }

      // Create error object with safe message
      const err = new Error(errorMessage)
      ;(err as any).status = resp.status
      ;(err as any).data = json
      throw err
    }

    if (shouldCache) {
      const safeData = clonePayload(json)
      // Store in both caches
      requestCache.set(cacheKey, { timestamp: Date.now(), ttl, data: safeData })
      apiCache.set(cacheKey, safeData, ttl)
    }

    return json
  })()

  if (shouldCache) {
    requestCache.set(cacheKey, { timestamp: Date.now(), ttl, promise: networkPromise })
    networkPromise.catch(() => invalidateCacheEntry(cacheKey))
  }

  return networkPromise
}

export async function login(email: string, password: string) {
  // Support both token-based (default) and Sanctum session-based auth
  if (USE_SANCTUM) {
    // For Sanctum session flow we must obtain CSRF cookie first
    try {
      await fetch(API_BASE.replace(/\/+$/, '') + '/sanctum/csrf-cookie', { credentials: 'include' })
    } catch {
      // continue — server may not need explicit CSRF endpoint depending on setup
    }

    // perform login with cookies
    const body = await apiFetch('/login', {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })

    // Sanctum flow usually doesn't return Bearer tokens — caller should rely on cookies
    return body
  }

  // Token-based default flow
  const body = await apiFetch('/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  })

  if (body && (body.access_token || body.token))
    setToken((body.access_token || (body as any).token) as string)
  return body
}

export async function logout() {
  try {
    await apiFetch('/logout', { method: 'POST' })
  } finally {
    setToken(null)
  }
}

export async function revalidateUser() {
  // Try fetching /user to check session/token validity
  try {
    const u = await apiFetch('/user')
    return u
  } catch {
    return null
  }
}

export const get = async (url: string, options: ApiFetchOptions = {}) => ({
  data: await apiFetch(url, { ...options, method: 'GET' }),
})
export const post = async (url: string, data?: any, options: ApiFetchOptions = {}) => ({
  data: await apiFetch(url, { ...options, method: 'POST', body: JSON.stringify(data) }),
})
export const put = async (url: string, data?: any, options: ApiFetchOptions = {}) => ({
  data: await apiFetch(url, { ...options, method: 'PUT', body: JSON.stringify(data) }),
})
export const patch = async (url: string, data?: any, options: ApiFetchOptions = {}) => ({
  data: await apiFetch(url, { ...options, method: 'PATCH', body: JSON.stringify(data) }),
})
export const del = async (url: string, options: ApiFetchOptions = {}) => ({
  data: await apiFetch(url, { ...options, method: 'DELETE' }),
})

export default {
  API_BASE,
  apiFetch,
  login,
  logout,
  setToken,
  getToken,
  revalidateUser,
  invalidateApiCache,
  USE_SANCTUM,
  get,
  post,
  put,
  patch,
  delete: del,
}
